import { useMemo } from 'react';
import { FeatureFlagProvider, toFlagEnvironment } from '@duke-hq/flags/react';
import { useAuthAdmin } from './AuthProviderAdmin';
import { usePostHog } from 'posthog-js/react';
import { useTenantContext } from './TenantContext';
import { trpcAdmin } from './lib/trpcAdmin';

export function WithFlags({ children }) {
  const { user } = useAuthAdmin();
  const ctx = useTenantContext();
  const posthog = usePostHog();

  // Prime cache with SSR flags, then rely on SSE updates from WithNotifications
  const flagsQuery = trpcAdmin.notifications.flags.useQuery(undefined, {
    // Use context flags as initial data to skip the first API call
    initialData: ctx?.flags
      ? {
          flags: ctx.flags,
          timestamp: Date.now(),
        }
      : undefined,
    // Consider data fresh for 1 hour — SSE handles updates, this is a fallback
    staleTime: 1000 * 60 * 60,
    // Keep in cache for 1 hour
    gcTime: 1000 * 60 * 60,
    // Refetch on window focus as a safety net if the SSE connection drops silently
    refetchOnWindowFocus: true,
    // Don't refetch on reconnect - SSE handles updates
    refetchOnReconnect: false,
    // Don't refetch on mount - we have fresh data from SSR
    refetchOnMount: false,
  });

  const normalizedEnvironment = toFlagEnvironment(ctx?.environment);

  const providerData = useMemo(
    () => ({
      user: user && {
        drn: user.drn,
      },
      environment: normalizedEnvironment,
      initialFlags: ctx?.flags ?? {},
      isIdentified: Boolean(user && user.drn !== undefined),
      posthog,
      debug: ctx?.debug ?? false,
    }),
    [user, normalizedEnvironment, ctx?.flags, posthog, ctx?.debug],
  );

  return (
    <FeatureFlagProvider
      data={providerData}
      flags={ctx?.flags ?? {}}
      api_key={ctx?.apis?.POSTHOG_KEY}
      flagsQueryResult={flagsQuery}
    >
      {children}
    </FeatureFlagProvider>
  );
}
